import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowRight, MapPin, Phone, Mail } from 'lucide-react';

const locationData = {
  toronto: {
    name: 'Toronto',
    province: 'Ontario',
    address: '123 Bay Street, Suite 2000, Toronto, ON M5J 2N8',
    description: 'Canada\'s largest tech hub and financial center, Toronto offers unmatched access to top IT talent and enterprise clients. Our Toronto office serves as our national headquarters, connecting businesses with cutting-edge AI solutions and skilled technology professionals.',
    highlights: [
      'Largest tech talent pool in Canada',
      'Financial services and fintech hub',
      'Access to Fortune 500 headquarters',
      'Leading AI and ML innovation center'
    ],
    industries: ['Financial Services', 'Technology', 'Healthcare', 'Retail', 'Professional Services'],
    phone: '(289) 633-4269',
    email: 'toronto@gentechstaffing.com'
  },
  vancouver: {
    name: 'Vancouver',
    province: 'British Columbia',
    address: '1055 West Georgia Street, Suite 1500, Vancouver, BC V6E 3P3',
    description: 'Vancouver\'s thriving tech ecosystem and proximity to Silicon Valley make it a strategic location for AI development, gaming, and digital media. We connect local businesses with specialized IT talent and advanced technology solutions.',
    highlights: [
      'Gaming and digital media expertise',
      'AI and machine learning innovation',
      'Green tech and sustainability focus',
      'Asia-Pacific gateway for tech talent'
    ],
    industries: ['Gaming', 'Digital Media', 'Clean Tech', 'Software Development', 'Film & Entertainment'],
    phone: '(289) 633-4269',
    email: 'vancouver@gentechstaffing.com'
  },
  calgary: {
    name: 'Calgary',
    province: 'Alberta',
    address: '525 8th Avenue SW, Suite 4200, Calgary, AB T2P 1G1',
    description: 'Calgary\'s energy sector transformation and growing tech scene create unique opportunities for IT staffing and digital transformation. We help oil & gas companies modernize operations with AI, automation, and cloud solutions.',
    highlights: [
      'Energy sector digital transformation',
      'Industrial IoT and automation',
      'Growing startup ecosystem',
      'Cloud and data analytics expertise'
    ],
    industries: ['Energy & Oil/Gas', 'Engineering', 'Construction', 'Logistics', 'Professional Services'],
    phone: '(289) 633-4269',
    email: 'calgary@gentechstaffing.com'
  },
  montreal: {
    name: 'Montreal',
    province: 'Quebec',
    address: '1250 René-Lévesque Blvd W, Suite 2200, Montreal, QC H3B 4W8',
    description: 'Montreal is a global AI research powerhouse and innovation hub. Home to world-class universities and AI institutes, we connect businesses with top-tier AI researchers, ML engineers, and bilingual technology professionals.',
    highlights: [
      'World-leading AI research hub',
      'Bilingual tech talent (English/French)',
      'Gaming and VFX industry strength',
      'Aerospace and advanced manufacturing'
    ],
    industries: ['Artificial Intelligence', 'Gaming', 'Aerospace', 'Pharmaceuticals', 'VFX & Animation'],
    phone: '(289) 633-4269',
    email: 'montreal@gentechstaffing.com'
  },
  ottawa: {
    name: 'Ottawa',
    province: 'Ontario',
    address: '150 Elgin Street, Suite 1400, Ottawa, ON K2P 1L4',
    description: 'As Canada\'s capital and a government technology center, Ottawa offers deep expertise in cybersecurity, telecommunications, and public sector IT. We provide security-cleared professionals and enterprise solutions for government and private sector clients.',
    highlights: [
      'Cybersecurity expertise',
      'Government IT and defense',
      'Telecommunications hub',
      'Security-cleared professionals'
    ],
    industries: ['Government', 'Defense', 'Cybersecurity', 'Telecommunications', 'Healthcare'],
    phone: '(289) 633-4269',
    email: 'ottawa@gentechstaffing.com'
  },
  edmonton: {
    name: 'Edmonton',
    province: 'Alberta',
    address: '10180 101 Street, Suite 3200, Edmonton, AB T5J 3S4',
    description: 'Edmonton\'s focus on AI, machine learning, and industrial automation makes it a key innovation center. We help businesses leverage technology for operational excellence in energy, manufacturing, and logistics.',
    highlights: [
      'AI and machine learning research',
      'Industrial automation expertise',
      'Energy sector innovation',
      'Emerging tech startup scene'
    ],
    industries: ['Energy', 'Manufacturing', 'Logistics', 'Agriculture Tech', 'Healthcare'],
    phone: '(289) 633-4269',
    email: 'edmonton@gentechstaffing.com'
  },
  'kitchener-waterloo': {
    name: 'Kitchener-Waterloo',
    province: 'Ontario',
    address: '305 King Street W, Suite 400, Kitchener, ON N2G 1B9',
    description: 'Known as Canada\'s Silicon Valley North, the Kitchener-Waterloo region is home to top universities and a dense startup ecosystem. We connect businesses with innovative tech talent and cutting-edge AI solutions.',
    highlights: [
      'University of Waterloo tech talent',
      'Strong startup and scale-up ecosystem',
      'Autonomous vehicle innovation',
      'Close proximity to Toronto market'
    ],
    industries: ['Software Development', 'Automotive Tech', 'IoT', 'Hardware', 'Insurance Tech'],
    phone: '(289) 633-4269',
    email: 'kw@gentechstaffing.com'
  },
  quebec: {
    name: 'Québec City',
    province: 'Quebec',
    address: '900 René-Lévesque Blvd E, Suite 500, Québec, QC G1R 2B5',
    description: 'Québec City combines government technology needs with a growing private sector tech scene. We provide bilingual IT professionals and digital transformation services for public and private organizations.',
    highlights: [
      'Government technology expertise',
      'Insurance and financial services',
      'Bilingual IT professionals',
      'Video game development'
    ],
    industries: ['Government', 'Insurance', 'Financial Services', 'Gaming', 'Tourism Tech'],
    phone: '(289) 633-4269',
    email: 'quebec@gentechstaffing.com'
  },
  saskatoon: {
    name: 'Saskatoon',
    province: 'Saskatchewan',
    address: '410 22nd Street E, Suite 810, Saskatoon, SK S7K 5T6',
    description: 'Saskatoon\'s agricultural technology and mining innovation sectors create unique IT opportunities. We connect businesses with specialized talent in ag-tech, resource optimization, and industrial automation.',
    highlights: [
      'Agricultural technology hub',
      'Mining and resource tech',
      'University research partnerships',
      'Industrial automation focus'
    ],
    industries: ['Agriculture Tech', 'Mining', 'Biotechnology', 'Manufacturing', 'Healthcare'],
    phone: '(289) 633-4269',
    email: 'saskatoon@gentechstaffing.com'
  }
};

const Location = () => {
  const { city } = useParams();
  const location = locationData[city?.toLowerCase()];

  if (!location) {
    return <Navigate to="/" replace />;
  }

  // Map city-specific hero images
  const getCityImage = (cityName) => {
    const imageMap = {
      'toronto': 'https://images.unsplash.com/photo-1517935706615-2717063c2225',
      'vancouver': 'https://images.unsplash.com/photo-1559827260-dc66d52bef19',
      'calgary': 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c',
      'montreal': 'https://images.unsplash.com/photo-1519389950473-47ba0277781c',
      'ottawa': 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab',
      'edmonton': 'https://images.unsplash.com/photo-1449157291145-7efd050a4d0e',
      'kitchener-waterloo': 'https://images.unsplash.com/photo-1497366754035-f200968a6e72',
      'quebec': 'https://images.unsplash.com/photo-1531973576160-7125cd663d86',
      'saskatoon': 'https://images.unsplash.com/photo-1486325212027-8081e485255e'
    };
    return imageMap[cityName] || 'https://images.unsplash.com/photo-1497366216548-37526070297c';
  };

  return (
    <div className="dark-page">
      <section className="location-hero">
        <div className="hero-image-wrapper">
          <img 
            src={getCityImage(city?.toLowerCase())} 
            alt={`${location.name} cityscape`}
            className="hero-background-image"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="location-hero-content">
          <h1 className="display-medium">{location.name} Recruitment Agencies</h1>
          <p className="body-large">Source the right talent for your project</p>
          
          <div className="location-cta-buttons">
            <Link to="/contact" className="btn-primary">
              Hire Talent
            </Link>
            <Link to="/contact" className="btn-secondary">
              Find Your Next Role
            </Link>
          </div>

          <div className="location-contact-header">
            <div className="location-info-item">
              <MapPin size={20} />
              <span>{location.address}</span>
            </div>
            <div className="location-info-item">
              <Phone size={20} />
              <span>{location.phone}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="location-content-section">
        <div className="location-main">
          <div className="location-description">
            <h2 className="display-medium">Why {location.name}?</h2>
            <p className="body-large">{location.description}</p>
          </div>

          <div className="location-highlights">
            <h3 className="heading-2">Local Strengths</h3>
            <div className="highlights-grid">
              {location.highlights.map((highlight, idx) => (
                <div key={idx} className="highlight-card">
                  <p className="body-medium">{highlight}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="location-industries">
            <h3 className="heading-2">Industries We Serve in {location.name}</h3>
            <div className="industries-tags">
              {location.industries.map((industry, idx) => (
                <span key={idx} className="industry-tag-large">
                  {industry}
                </span>
              ))}
            </div>
          </div>

          <div className="location-services">
            <h3 className="heading-2">Our Services in {location.name}</h3>
            <div className="services-list-compact">
              <div className="service-column">
                <h4 className="heading-3">Technology & Consulting</h4>
                <ul className="service-simple-list">
                  <li>IT Staffing Solutions</li>
                  <li>Robotic Process Automation</li>
                  <li>ERP / CRM Implementation</li>
                  <li>Custom Application Development</li>
                  <li>AI Strategy & Readiness</li>
                  <li>Generative AI / LLM Implementation</li>
                  <li>Data Engineering & MLOps</li>
                </ul>
              </div>
              <div className="service-column">
                <h4 className="heading-3">Workforce Solutions</h4>
                <ul className="service-simple-list">
                  <li>Employer of Record (EOR)</li>
                  <li>Agent of Record (AOR)</li>
                  <li>Direct Sourcing</li>
                  <li>Global Talent Solutions</li>
                  <li>Contract Staffing</li>
                  <li>Permanent Placement</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="location-contact-block">
            <h3 className="heading-2">Connect With Our {location.name} Team</h3>
            <div className="location-contact-info">
              <div className="contact-info-item">
                <MapPin size={24} className="contact-icon" />
                <div>
                  <p className="body-medium contact-label">Location</p>
                  <p className="body-large contact-value">{location.name}, {location.province}</p>
                </div>
              </div>
              <div className="contact-info-item">
                <Phone size={24} className="contact-icon" />
                <div>
                  <p className="body-medium contact-label">Phone</p>
                  <p className="body-large contact-value">{location.phone}</p>
                </div>
              </div>
              <div className="contact-info-item">
                <Mail size={24} className="contact-icon" />
                <div>
                  <p className="body-medium contact-label">Email</p>
                  <p className="body-large contact-value">{location.email}</p>
                </div>
              </div>
            </div>
            <Link to="/contact" className="btn-primary btn-large">
              Get Started in {location.name}
              <ArrowRight size={24} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Location;
